export type VideoEmbedKind = "youtube" | "vimeo" | "link";
export type BlockMediaKind = VideoEmbedKind | "image";

export type VideoEmbedInfo =
  | { kind: "youtube"; embedUrl: string }
  | { kind: "vimeo"; embedUrl: string }
  | { kind: "link"; href: string };

export type BlockMediaInfo =
  | VideoEmbedInfo
  | { kind: "image"; src: string };

/** Extracts a YouTube video ID from common URL formats. */
export const parseYoutubeVideoId = (
  url: string | null | undefined,
): string | null => {
  if (!url?.trim()) return null;
  const u = url.trim();

  const short = u.match(/youtu\.be\/([a-zA-Z0-9_-]{11})(?:\?|$)/);
  if (short?.[1]) return short[1];

  const watch = u.match(/[?&]v=([a-zA-Z0-9_-]{11})(?:&|$)/);
  if (watch?.[1]) return watch[1];

  const embed = u.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})(?:\?|$)/);
  if (embed?.[1]) return embed[1];

  const shorts = u.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})(?:\?|$)/);
  if (shorts?.[1]) return shorts[1];

  return null;
};

export const youtubeEmbedUrl = (videoId: string): string =>
  `https://www.youtube.com/embed/${videoId}?rel=0`;

/** Extracts a Vimeo video ID from common URL formats. */
export const parseVimeoVideoId = (
  url: string | null | undefined,
): string | null => {
  if (!url?.trim()) return null;
  const u = url.trim();
  const match = u.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\?|$|\/)/);
  return match?.[1] ?? null;
};

export const vimeoEmbedUrl = (videoId: string): string =>
  `https://player.vimeo.com/video/${videoId}`;

const HTTP_URL_PATTERN = /^https?:\/\/[^\s/][^\s]*$/i;
const HTTP_IMAGE_URL_PATTERN =
  /^https?:\/\/[^\s/][^\s]*\.(jpe?g|png|webp|gif|heic|heif)(\?[^\s]*)?$/i;

export const isDataImageUrl = (value: string | null | undefined): boolean =>
  !!value?.trim().startsWith("data:image/");

export const isHttpUrl = (value: string | null | undefined): boolean => {
  if (!value?.trim()) return false;
  return HTTP_URL_PATTERN.test(value.trim());
};

export const isHttpImageUrl = (value: string | null | undefined): boolean => {
  if (!isHttpUrl(value)) return false;
  return HTTP_IMAGE_URL_PATTERN.test(value!.trim());
};

export const isImageMediaUrl = (value: string | null | undefined): boolean =>
  isDataImageUrl(value) || isHttpImageUrl(value);

export const normalizeMediaUrl = (
  value: string | null | undefined,
): string | null => {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  if (isDataImageUrl(trimmed)) return trimmed;
  return isHttpUrl(trimmed) ? trimmed : null;
};

export const getBlockMediaInfo = (
  url: string | null | undefined,
): BlockMediaInfo | null => {
  const normalized = normalizeMediaUrl(url);
  if (!normalized) return null;

  if (isImageMediaUrl(normalized)) {
    return { kind: "image", src: normalized };
  }

  const youtubeId = parseYoutubeVideoId(normalized);
  if (youtubeId) {
    return { kind: "youtube", embedUrl: youtubeEmbedUrl(youtubeId) };
  }

  const vimeoId = parseVimeoVideoId(normalized);
  if (vimeoId) {
    return { kind: "vimeo", embedUrl: vimeoEmbedUrl(vimeoId) };
  }

  return { kind: "link", href: normalized };
};

export const getVideoEmbedInfo = (
  url: string | null | undefined,
): VideoEmbedInfo | null => {
  const info = getBlockMediaInfo(url);
  if (!info || info.kind === "image") return null;
  return info;
};
